import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from "react-hot-toast"; // Correct import for toast
import logoes from "../assets/Logos.png";
import {
  HomeIcon,
  InboxIcon,
  HeartIcon as HeartOutline,
  CogIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolid } from "@heroicons/react/24/solid";
import languageLogo from "../assets/Icons/language-icon-matrimony.png";
import NavPointsIcon from "../assets/Icons/points-icon.svg";
import NavDropdownIcon from "../assets/Icons/arrow-down.svg";

let profileCache = {
  data: null,
  timestamp: null,
  isLoading: false,
};

const CACHE_DURATION = 5 * 60 * 1000;

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [points, setPoints] = useState();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const dropdownRefLanguage = useRef(null);
  const dropdownRefProfile = useRef(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));

  useEffect(() => {
    const handleUpdatePoints = (event) => {
      setPoints(event.detail); // Changed from setCurrentPoints to setPoints
    };

    window.addEventListener("updatePoints", handleUpdatePoints);

    return () => {
      window.removeEventListener("updatePoints", handleUpdatePoints);
    };
  }, []);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutsideLanguage = (event) => {
      if (
        dropdownRefLanguage.current &&
        !dropdownRefLanguage.current.contains(event.target)
      ) {
        setShowLanguageDropdown(false);
      }
    };

    const handleClickOutsideProfile = (event) => {
      if (
        dropdownRefProfile.current &&
        !dropdownRefProfile.current.contains(event.target)
      ) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideLanguage);
    document.addEventListener("mousedown", handleClickOutsideProfile);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideLanguage);
      document.removeEventListener("mousedown", handleClickOutsideProfile);
    };
  }, []);

  // Fetch points when language changes
  const fetchPoints = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        "https://backend.newbrindha.com/wallet/balance",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 401) {
        localStorage.removeItem("authToken");
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error(t("fetchPointsError"));
      }

      const data = await response.json();
      setPoints(data.points_balance);
    } catch (error) {
      console.error("Error fetching points:", error.message);
      toast.error(t("fetchPointsError"));
    }
  };

  useEffect(() => {
    fetchPoints();
  }, [i18n.language]);

  

  // Replace your existing fetchUserProfile function with this:
  const fetchUserProfile = async (forceRefresh = false) => {
    const now = Date.now();

    // Check if we have cached data and it's still valid
    if (
      !forceRefresh &&
      profileCache.data &&
      profileCache.timestamp &&
      now - profileCache.timestamp < CACHE_DURATION
    ) {
      setUserProfile(profileCache.data);
      return;
    }

    // Prevent multiple simultaneous requests
    if (profileCache.isLoading) {
      return;
    }

    try {
      profileCache.isLoading = true;
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(
        "https://backend.newbrindha.com/matrimony/my_profiles",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 401) {
        localStorage.removeItem("authToken");
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error(t("fetchProfileError"));
      }

      const data = await response.json();

      // Cache the data
      profileCache.data = data.profile;
      profileCache.timestamp = now;

      setUserProfile(data.profile);
    } catch (error) {
      console.error("Error fetching profile:", error.message);
      toast.error(t("fetchProfileError"));
    } finally {
      profileCache.isLoading = false;
    }
  };

  // Replace your existing useEffect for profile fetching with this:
  useEffect(() => {
    fetchUserProfile(); // This will now use cache when available
  }, []);

  useEffect(() => {
    const handleUpdatePoints = (event) => {
      setPoints(event.detail);
    };

    window.addEventListener("updatePoints", handleUpdatePoints);

    return () => {
      window.removeEventListener("updatePoints", handleUpdatePoints);
    };
  }, []);

  // Language change handler
  const toggleLanguageDropdown = () => {
    setShowLanguageDropdown((prev) => !prev);
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setShowLanguageDropdown(false);
  };

  // Profile dropdown toggle
  const toggleProfileDropdown = () => {
    setShowProfileDropdown((prev) => !prev);
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem("authToken");

    profileCache = {
      data: null,
      timestamp: null,
      isLoading: false,
    };

    navigate("/login");
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem("authToken");
      if (currentToken !== authToken) {
        setAuthToken(currentToken);
        fetchUserProfile(true); // force refresh
      }
    }, 1000); // poll every second

    return () => clearInterval(interval);
  }, [authToken]);

  // Contact form submission handler
  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const contactData = {
      full_name: formData.get("name"),
      email: formData.get("email"),
      message: formData.get("message"),
    };

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        "https://backend.newbrindha.com/matrimony/contact-us",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(contactData),
        },
      );

      if (!response.ok) {
        throw new Error(t("contactFormError"));
      }

      setIsContactFormOpen(false);
      toast.success(t("contactFormSuccess"));
    } catch (error) {
      console.error("Error submitting contact form:", error.message);
      toast.error(t("contactFormError"));
    }
  };

  // Animation variants for modal
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
  };

  return (
    <div>
      <Toaster position="top-right" reverseOrder={false} />{" "}
      
      <header className="fixed top-0 z-50 bg-[#ff8350] px-3 sm:px-4 w-full py-1"> 
        <nav className="flex items-center justify-between">
          <div className="flex items-center space-x-2 w-fit">
            <img
              src={logoes}
              alt="Dashboard Logo"
              className="h-[5rem] w-[5rem] sm:h-[4rem] sm:w-[4rem] hover:scale-110"
            />
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="md:hidden"
            >
              <Bars3Icon className="h-6 w-6 text-white" />
            </button>
          </div>

          <ul className="hidden md:flex items-center space-x-2 text-white m-0 w-fit">
            <li
              onClick={() => navigate("/dashboard")}
              className="flex items-center px-3 py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group"
            >
              <span>{t("home")}</span>
            </li>
            <li
              onClick={() => navigate("/matchingProfiles")}
              className="flex items-center px-3 py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group"
            >
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap">
                  {t("matchingProfiles")}
                </span>
              </div>
            </li>
            <li
              onClick={() => navigate("/favourite")}
              className="flex items-center px-3 py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group"
            >
              <span>{t("favourites")}</span>
            </li>
            <li
              onClick={() => navigate("/about")}
              className="flex items-center px-3 py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group"
            >
              <span>{t("aboutus")}</span>
            </li>
            <li
              onClick={() => navigate("/how")}
              className="flex items-center px-3 py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group"
            >
              <span>{t("howits")}</span>
            </li>
          </ul>

          <div className="flex items-center gap-2 sm:space-x-2">
            {/* Points Button */}
            <button
              onClick={() => navigate("/points")}
              className="flex items-center justify-center gap-1.5 text-white font-semibold hover:bg-white py-2 px-2 group transition-all duration-150 ease-in-out transform hover:scale-[1.02] rounded-lg"
            >
              <img
                src={NavPointsIcon}
                alt="points-icon"
                className="w-6 h-6 sm:h-7 sm:w-7"
              />
              <span className="group-hover:text-[#676666]">
                {points || "0"}
              </span>
            </button>

            <section className="flex items-center justify-center gap-2.5 sm:gap-4">
              {/* Language Toggle Button and Dropdown */}
              <div className="relative" ref={dropdownRefLanguage}>
                <button
                  onClick={toggleLanguageDropdown}
                  className="bg-white text-[#bb5a68] p-1.5 rounded-full font-medium border border-[#bc5274] hover:bg-[#bb5a68] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f39c12] transition-all duration-150 ease-in-out transform hover:scale-[1.02] shadow-md shadow-[#f39c12]/20 hover:shadow-[#f39c12]/30"
                >
                  <img
                    src={languageLogo}
                    alt="language-logo"
                    className="w-6 h-6 sm:h-6 sm:w-6 md:h-6 md:w-6"
                  />
                </button>

                {showLanguageDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-[#f39c12]/20 z-50"
                  >
                    <button
                      onClick={() => handleLanguageChange("en")}
                      className="w-full text-left px-4 py-2 text-[#d35400] hover:bg-[#f39c12] hover:text-white rounded-t-lg"
                    >
                      English
                    </button>
                    <button
                      onClick={() => handleLanguageChange("ta")}
                      className="w-full text-left px-4 py-2 text-[#d35400] hover:bg-[#f39c12] hover:text-white rounded-b-lg"
                    >
                      தமிழ்
                    </button>
                  </motion.div>
                )}
              </div>

              {/* Profile Dropdown */}
              <div className="relative" ref={dropdownRefProfile}>
                <div
                  onClick={toggleProfileDropdown}
                  className="flex items-center justify-center gap-1 cursor-pointer z-[999]"
                >
                  <button className="w-[36px] h-[36px] rounded-full overflow-hidden transition-all duration-200 shadow-lg">
                    {userProfile?.photo_path ? (
                      <img
                        src={userProfile.photo_path}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "/default-placeholder.png"; // Fallback image
                        }}
                      />
                    ) : (
                      <UserCircleIcon className="h-9 w-9 text-[#c7c0c0e4]" />
                    )}
                  </button>
                  <img
                    src={NavDropdownIcon}
                    alt="nav-profile-dropdown-icon"
                    className="w-6 h-6 sm:h-6 sm:w-6 md:h-6 md:w-6 mt-2"
                  />
                </div>
                {showProfileDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-52 p-2 bg-white rounded-lg shadow-lg border border-[#f39c12]/20 z-50"
                  >
                    <button
                      onClick={() => navigate("/my")}
                      className="w-full text-left px-2 py-2 text-[#404040] hover:bg-gray-100 rounded-lg"
                    >
                      {t("myProfile")}
                    </button>
                    <button
                      onClick={() => setIsContactFormOpen(true)}
                      className="w-full text-left px-2 py-2 text-[#404040] hover:bg-gray-100 rounded-lg"
                    >
                      {t("contactUs")}
                    </button>
                    {/* <button
                      onClick={() => navigate("/dg")}
                      className="w-full text-left px-2 py-2 text-[#404040] hover:bg-gray-100 rounded-lg"
                    >
                      {t("settings.settingstitle")}
                    </button> */}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-2 py-2 text-[#404040] hover:bg-gray-100 rounded-lg"
                    >
                      {t("logout")}
                    </button>
                  </motion.div>
                )}
              </div>
            </section>
          </div>
        </nav>
      </header>
      <div>
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          ></div>
        )}

        <aside
          className={`md:hidden fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#FF8350] text-white transform transition-transform duration-300 ease-in-out z-50 ${
            isSidebarOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }`}
        >
          <div className="h-full flex flex-col relative">
            <div className="p-6 flex justify-between items-center">
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="absolute top-4 right-4 md:hidden text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
{/* 
            <div className="px-2 flex items-center justify-start">
              <img
                src={logoes}
                alt="Dashboard Logo"
                className="h-24 w-22 sm:h-22 sm:w-22 animate-pulse transition-transform duration-500 hover:scale-110"
              />
            </div> */}

            <nav className="flex flex-col items-start justify-start space-y-2 overflow-y-auto">
              <ul className="space-y-2">
                <li
                  onClick={() => {
                    navigate("/dashboard");
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group cursor-pointer"
                >
                  <HomeIcon className="h-5 w-5 mr-2" />
                  <span>{t("home")}</span>
                </li>
                <li
                  onClick={() => {
                    navigate("/matchingProfiles");
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group cursor-pointer"
                >
                  <InboxIcon className="h-5 w-5 mr-2" />
                  <span>{t("matchingProfiles")}</span>
                </li>
                <li
                  onClick={() => {
                    navigate("/favourite");
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group cursor-pointer"
                >
                  <HeartOutline className="h-5 w-5 mr-2" />
                  <span>{t("favourites")}</span>
                </li>
                {/* <li
                  onClick={() => {
                    navigate("/setting");
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group cursor-pointer"
                >
                  <CogIcon className="h-5 w-5 mr-2" />
                  <span>{t("settings.settingstitle")}</span>
                </li> */}
                <li
                  onClick={() => {
                    navigate("/about");
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group cursor-pointer"
                >
                  <CogIcon className="h-5 w-5 mr-2" />
                  <span>{t("aboutus")}</span>
                </li>
                <li
                  onClick={() => {
                    navigate("/how");
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group cursor-pointer"
                >
                  <CogIcon className="h-5 w-5 mr-2" />
                  <span>{t("howits")}</span>
                </li>
                <li
                  onClick={() => {
                    setIsContactFormOpen(true);
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group cursor-pointer"
                >
                  <InboxIcon className="h-5 w-5 mr-2" />
                  <span>{t("contactUs")}</span>
                </li>
                <li
                  onClick={() => {
                    handleLogout();
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center py-2 rounded-lg hover:bg-white hover:text-[#676666] transition-all duration-300 hover:scale-105 hover:bg-opacity-90 group cursor-pointer"
                >
                  <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-2" />
                  <span>{t("logout")}</span>
                </li>
              </ul>
            </nav>
          </div>
        </aside>

        {/* Contact Form Modal */}
        <AnimatePresence>
          {isContactFormOpen && (
            <motion.div
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={modalVariants}
            >
              <motion.div
                className="bg-white rounded-xl max-w-md w-full p-6"
                variants={modalVariants}
              >
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t("contactUs")}
                  </h2>
                  <button
                    onClick={() => setIsContactFormOpen(false)}
                    className="p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleContactFormSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("name")}
                    </label>
                    <input
                      type="text"
                      name="name"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FFA500] focus:border-transparent transition-all duration-200"
                      placeholder={t("name")}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("email")}
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FFA500] focus:border-transparent transition-all duration-200"
                      placeholder={t("email")}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("form.message")}
                    </label>
                    <textarea
                      name="message"
                      rows="4"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#FFA500] focus:border-transparent transition-all duration-200"
                      placeholder={t("form.message")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full px-4 py-2 bg-[#ff8350] text-white rounded-lg hover:from-[#FFD54F] hover:to-[#FFA500] transition-all duration-200 shadow-lg"
                  >
                    {t("sendMessage")}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Navbar;
